/**
 * OP-7: Alerts Page
 * View and manage alerts
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { 
  RefreshCw, Loader2, Bell, AlertTriangle, CheckCircle, 
  Search, Thermometer, Clock, Eye
} from 'lucide-react';

export default function OpsAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      // Fetch ALL alerts so KPI counters reflect true totals; filtering is done client-side.
      const response = await opsApi.getAlerts({});
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    setActionLoading(true);
    try {
      await opsApi.acknowledgeAlert(alertId);
      toast.success('Alert acknowledged');
      fetchAlerts();
      setSelectedAlert(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async (alertId) => {
    setActionLoading(true);
    try {
      await opsApi.resolveAlert(alertId);
      toast.success('Alert resolved');
      fetchAlerts();
      setSelectedAlert(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const matchesStatus = statusFilter === 'all' || alert.status === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchesQuery = !q || (alert.message?.toLowerCase().includes(q) || alert.device_uid?.toLowerCase().includes(q));
    return matchesStatus && matchesQuery;
  });

  const getLevelBadge = (level) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-400 border-red-500',
      warning: 'bg-amber-500/20 text-amber-400 border-amber-500',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500'
    };
    return <Badge variant="outline" className={styles[level] || styles.info}>{level}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-500/20 text-red-400 border-red-500',
      acknowledged: 'bg-amber-500/20 text-amber-400 border-amber-500',
      resolved: 'bg-green-500/20 text-green-400 border-green-500'
    };
    return <Badge variant="outline" className={styles[status] || styles.open}>{status}</Badge>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'temp_breach':
        return <Thermometer className="w-4 h-4 text-red-400" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    }
  };

  // Count stats
  const openCount = alerts.filter(a => a.status === 'open').length;
  const acknowledgedCount = alerts.filter(a => a.status === 'acknowledged').length;
  const resolvedCount = alerts.filter(a => a.status === 'resolved').length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Alerts</h1>
          <p className="text-slate-400">Monitor and manage system alerts</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchAlerts}
          className="border-slate-700 text-slate-300"
          data-testid="refresh-alerts"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button
          onClick={() => setStatusFilter('open')}
          className={`bg-slate-900 rounded-xl p-4 border transition-colors ${
            statusFilter === 'open' ? 'border-red-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{openCount}</p>
              <p className="text-slate-400 text-sm">Open</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('acknowledged')}
          className={`bg-slate-900 rounded-xl p-4 border transition-colors ${
            statusFilter === 'acknowledged' ? 'border-amber-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Eye className="w-5 h-5 text-amber-400" />
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{acknowledgedCount}</p>
              <p className="text-slate-400 text-sm">Acknowledged</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('resolved')}
          className={`bg-slate-900 rounded-xl p-4 border transition-colors ${
            statusFilter === 'resolved' ? 'border-green-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{resolvedCount}</p>
              <p className="text-slate-400 text-sm">Resolved</p>
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="alert-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-white">All Alerts</SelectItem>
            <SelectItem value="open" className="text-white">Open</SelectItem>
            <SelectItem value="acknowledged" className="text-white">Acknowledged</SelectItem>
            <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search alerts..."
            className="pl-10 bg-slate-900 border-slate-700 text-white"
            data-testid="alert-search"
          />
        </div>
      </div>

      {/* Alerts Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Type</TableHead>
              <TableHead className="text-slate-400">Message</TableHead>
              <TableHead className="text-slate-400">Level</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Triggered</TableHead>
              <TableHead className="text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredAlerts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  No alerts found
                </TableCell>
              </TableRow>
            ) : (
              filteredAlerts.map((alert) => (
                <TableRow 
                  key={alert.id} 
                  className="border-slate-800"
                  data-testid={`alert-row-${alert.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getTypeIcon(alert.type)}
                      <span className="text-slate-300 text-sm capitalize">
                        {alert.type?.replace('_', ' ') || 'Alert'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-white max-w-md">
                    <p className="truncate">{alert.message}</p>
                    {alert.device_uid && (
                      <p className="text-slate-500 text-xs mt-1">Device: {alert.device_uid}</p>
                    )}
                  </TableCell>
                  <TableCell>{getLevelBadge(alert.level)}</TableCell>
                  <TableCell>{getStatusBadge(alert.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-slate-400 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(alert.triggered_at).toLocaleString()}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {alert.status === 'open' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAcknowledge(alert.id)}
                          className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                          data-testid={`ack-alert-${alert.id}`}
                        >
                          Acknowledge
                        </Button>
                      )}
                      {alert.status !== 'resolved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolve(alert.id)}
                          className="border-green-500 text-green-400 hover:bg-green-500/20"
                          data-testid={`resolve-alert-${alert.id}`}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
